import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "./api";
import { ymd, formatEventDate, formatTime12 as formatTime, getRsvpLabel } from "./utils";
import RecentRsvpsTile from "./RecentRsvpsTile";
import { FaCheck, FaDumbbell, FaCalendarAlt, FaClock, FaMapMarkerAlt, FaUsers, FaStickyNote, FaTrophy, FaMedal, FaHistory, FaSitemap } from "react-icons/fa";
import { GiCanoe, GiPaddles } from "react-icons/gi";
import "./AthleteHomePage.css";
import "./AvailabilityModal.css";

export default function AthleteHomePage({
  user,
  onRsvpClick,
  onLogWorkoutClick,
  onPracticeClick,
  onLineupClick,
  onOyoWorkoutClick,
  onMaintenanceClick,
  refreshKey
}) {
  const [stats, setStats] = useState({
    workoutsLogged: 0,
    practicesAttended: 0,
    metersLogged: 0,
    attendancePercent: null
  });
  const [nextEvents, setNextEvents] = useState([]);
  const [regattas, setRegattas] = useState([]);
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingNextEvent, setLoadingNextEvent] = useState(false);
  const [savingRegatta, setSavingRegatta] = useState({});
  const [leaderboard, setLeaderboard] = useState([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);
  const [leaderboardTab, setLeaderboardTab] = useState("all");
  const [leaderboardPeriod, setLeaderboardPeriod] = useState("30d");
  const [nextLineupPublished, setNextLineupPublished] = useState(false);
  const [nextBoatAssignment, setNextBoatAssignment] = useState(null);

  const LEADERBOARD_TABS = [
    { key: "all",  label: "All" },
    { key: "u19",  label: "U19", ageGroups: ["U19", "Open"] },
    { key: "u17",  label: "U17", ageGroups: ["U17"] },
    { key: "u16",  label: "U16", ageGroups: ["U16"] },
  ];

  const userTeam = typeof user?.team === "string" ? user.team.trim() : "";
  const userId = user?.id ? Number(user.id) : null;

  // Load athlete stats from dedicated endpoint
  const loadStats = useCallback(async () => {
    if (!userId) return;
    setLoadingStats(true);
    try {
      const stats = await apiFetch(`/athlete-stats/mine?as=${userId}`);
      setStats({
        workoutsLogged: stats.workoutsLogged || 0,
        practicesAttended: stats.practicesAttended || 0,
        metersLogged: stats.metersLogged || 0,
        attendancePercent: typeof stats.attendancePercent === "number" ? stats.attendancePercent : null
      });
    } catch (err) {
      console.error("Failed to load stats:", err);
      // Keep showing zeros on error
    } finally {
      setLoadingStats(false);
    }
  }, [userId]);

  // Load team leaderboard
  const loadLeaderboard = useCallback(async (tab, period) => {
    if (!userTeam) return;
    setLoadingLeaderboard(true);
    try {
      const tabDef = LEADERBOARD_TABS.find(t => t.key === tab) || LEADERBOARD_TABS[0];
      let url = `/athlete-stats/leaderboard?team=${encodeURIComponent(userTeam)}`;
      if (tabDef.ageGroups) {
        url += `&age_groups=${encodeURIComponent(tabDef.ageGroups.join(","))}`;
      }
      if (period === "all") url += `&period=all`;
      const rows = await apiFetch(url);
      setLeaderboard(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error("Failed to load leaderboard:", err);
      setLeaderboard([]);
    } finally {
      setLoadingLeaderboard(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userTeam]);

  // Load next event and regatta schedule
  const loadNextEvent = useCallback(async () => {
    if (!userId || !userTeam) return;
    setLoadingNextEvent(true);
    try {
      const today = new Date();
      const endDate = new Date(today);
      endDate.setMonth(endDate.getMonth() + 6); // Look ahead 6 months for regattas

      const from = ymd(today);
      const to = ymd(endDate);

      const params = new URLSearchParams({ from, to });
      if (userTeam) params.append("team", userTeam);

      const [practiceRows, attendanceRows] = await Promise.all([
        apiFetch(`/practices/range?${params.toString()}`),
        apiFetch(`/attendance/range?user=${userId}&from=${from}&to=${to}`)
      ]);

      const practices = Array.isArray(practiceRows) ? practiceRows : [];
      const attendance = Array.isArray(attendanceRows) ? attendanceRows : [];

      // Create attendance lookup by practice ID
      const attendanceMap = new Map(
        attendance.map(a => [a.practice_id, a.status])
      );

      // Normalize all practices
      const now = new Date();
      const normalizedAll = practices.map(p => ({
        ...p,
        startTime: p.startTime || p.start_time || p.time,
        endTime: p.endTime || p.end_time,
        squad: p.squad || p.squadName,
        rsvpStatus: attendanceMap.get(p.id) || null
      }));

      // Keep practices that are still upcoming OR currently in progress
      // (started but end time hasn't passed yet), matching coach view behavior.
      const normalized = normalizedAll
        .filter(p => {
          if (p.state === "off") return false;
          const start = p.date ? new Date(`${p.date}T${p.startTime || "00:00"}`) : null;
          const end = p.date && p.endTime ? new Date(`${p.date}T${p.endTime}`) : null;
          if (!start) return false;
          const inProgress = start <= now && (!end || end > now);
          return inProgress || start >= now;
        })
        .sort((a, b) => {
          const dateA = new Date(`${a.date}T${a.startTime || "00:00"}`);
          const dateB = new Date(`${b.date}T${b.startTime || "00:00"}`);
          return dateA - dateB;
        });

      // All events on the earliest upcoming/in-progress date
      const nextDate = normalized.length > 0 ? normalized[0].date : null;
      const upcoming = nextDate ? normalized.filter(p => p.date === nextDate) : [];
      setNextEvents(upcoming);

      // All upcoming regattas — deduplicate multi-day events (API expands them per day)
      const seenIds = new Set();
      const uniqueRegattas = normalized.filter(p => {
        if (p.type !== "regatta") return false;
        if (seenIds.has(p.id)) return false;
        seenIds.add(p.id);
        return true;
      });
      setRegattas(uniqueRegattas);
    } catch (err) {
      console.error("Failed to load next event:", err);
      setNextEvents([]);
      setRegattas([]);
    } finally {
      setLoadingNextEvent(false);
    }
  }, [userId, userTeam]);


  // Load data independently for faster perceived performance
  useEffect(() => {
    if (!userId) return;

    // Load all data in parallel - each section updates as it completes
    loadStats(); // Stats can load without team
  }, [userId, loadStats, refreshKey]);

  useEffect(() => {
    if (!userId || !userTeam) return;

    loadNextEvent(); // Next event requires team for practice filtering
    loadLeaderboard("all", "30d");
  }, [userId, userTeam, loadNextEvent, loadLeaderboard, refreshKey]);

  // Check whether a lineup has been published for the next practice's date
  const nextEventDate = nextEvents[0]?.date || null;
  useEffect(() => {
    if (!nextEventDate || !userTeam) {
      setNextLineupPublished(false);
      return;
    }
    let cancelled = false;
    apiFetch(`/lineups/${nextEventDate}/status?team=${encodeURIComponent(userTeam)}`)
      .then(status => {
        if (!cancelled) setNextLineupPublished(Boolean(status?.published));
      })
      .catch(() => {
        if (!cancelled) setNextLineupPublished(false);
      });
    return () => { cancelled = true; };
  }, [nextEventDate, userTeam]);

  // Once we know a lineup is published, find which boat (hull/oars) this athlete is seated in
  const nextEventId = nextEvents[0]?.id || null;
  useEffect(() => {
    if (!nextLineupPublished || !nextEventId || !userTeam || !userId) {
      setNextBoatAssignment(null);
      return;
    }
    let cancelled = false;
    apiFetch(`/lineups/${nextEventId}/published?team=${encodeURIComponent(userTeam)}`)
      .then(data => {
        if (cancelled) return;
        const boats = Array.isArray(data?.boats) ? data.boats : [];
        for (const boat of boats) {
          const mySeat = (boat.seats || []).find(s => s.userId === userId);
          if (mySeat) {
            setNextBoatAssignment({ hull: boat.hull || null, oars: boat.oars || null, seat: mySeat.seat || null });
            return;
          }
        }
        setNextBoatAssignment(null);
      })
      .catch(() => {
        if (!cancelled) setNextBoatAssignment(null);
      });
    return () => { cancelled = true; };
  }, [nextLineupPublished, nextEventId, userTeam, userId]);

  function handleLeaderboardTabChange(tabKey) {
    setLeaderboardTab(tabKey);
    loadLeaderboard(tabKey, leaderboardPeriod);
  }

  function handleLeaderboardPeriodChange(period) {
    setLeaderboardPeriod(period);
    loadLeaderboard(leaderboardTab, period);
  }

  function handleNextEventClick(event) {
    if (onPracticeClick && event.id) {
      // Pass all event data including name
      onPracticeClick(event.id, event.date, event.startTime || event.time, event.squad, event.endTime, event.name, event.location);
    }
  }

  async function handleRegattaRsvp(regattaId, newStatus) {
    if (!userId) return;
    if (savingRegatta[regattaId]) return;
    setSavingRegatta(prev => ({ ...prev, [regattaId]: true }));
    // Optimistic update
    setRegattas(prev =>
      prev.map(r => r.id === regattaId ? { ...r, rsvpStatus: newStatus } : r)
    );
    try {
      await apiFetch(`/attendance/mine?as=${userId}`, {
        method: "POST",
        body: JSON.stringify({ status: newStatus, practice_id: regattaId }),
      });
    } catch (err) {
      console.error("Failed to RSVP to regatta:", err);
      // Revert on failure
      await loadNextEvent();
    } finally {
      setSavingRegatta(prev => ({ ...prev, [regattaId]: false }));
    }
  }

  function formatRegattaDateRange(startDate, endDate) {
    const fmt = (d) => {
      const x = new Date(`${d}T00:00:00`);
      return x.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    };
    const start = fmt(startDate);
    if (!endDate || endDate === startDate) return start;
    const s = new Date(`${startDate}T00:00:00`);
    const e = new Date(`${endDate}T00:00:00`);
    if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
      return `${start} – ${e.getDate()}`;
    }
    return `${start} – ${fmt(endDate)}`;
  }

  function formatNumber(num) {
    return num.toLocaleString();
  }

  const primaryEvent = nextEvents[0] || null;
  const isInProgress = primaryEvent && (() => {
    const start = primaryEvent.date ? new Date(`${primaryEvent.date}T${primaryEvent.startTime || "00:00"}`) : null;
    const end = primaryEvent.date && primaryEvent.endTime ? new Date(`${primaryEvent.date}T${primaryEvent.endTime}`) : null;
    const now = new Date();
    return start && start <= now && (!end || end > now);
  })();
  const podiumOrder = [1, 0, 2]; // silver, gold, bronze visual order
  const topThree = leaderboard.slice(0, 3);
  const rest = leaderboard.slice(3);

  return (
    <div className="athlete-home-page">
      {/* Hero: current / next event */}
      <div className="home-hero">
        <div className="home-hero-top">
          <span className="home-hero-eyebrow">
            <span className="dot" />
            {isInProgress ? "Current Practice" : "Next Up"}
          </span>
          {isInProgress && <span className="home-hero-live">● Live</span>}
        </div>
        {loadingNextEvent ? (
          <div className="home-hero-empty">Loading...</div>
        ) : primaryEvent ? (
          <>
            <div className="home-hero-date">
              <FaCalendarAlt style={{ marginRight: 6 }} />
              {formatEventDate(primaryEvent.date)}
            </div>
            <div className="home-hero-title">{primaryEvent.name || "Practice"}</div>
            <div className="home-hero-meta-row">
              <span className="home-hero-chip">
                <FaClock />
                {formatTime(primaryEvent.startTime || primaryEvent.time)}
                {primaryEvent.endTime && ` – ${formatTime(primaryEvent.endTime)}`}
              </span>
              {primaryEvent.location && (
                <span className="home-hero-chip"><FaMapMarkerAlt />{primaryEvent.location}</span>
              )}
              {primaryEvent.squad && (
                <span className="home-hero-chip"><FaUsers />{primaryEvent.squad}</span>
              )}
              {primaryEvent.note && (
                <span className="home-hero-chip"><FaStickyNote />{primaryEvent.note}</span>
              )}
              {nextBoatAssignment?.hull && (
                <span className="home-hero-chip home-hero-hull">
                  <GiCanoe />
                  <span className="home-hero-hull-name">{nextBoatAssignment.hull}</span>
                </span>
              )}
              {nextBoatAssignment?.oars && (
                <span className="home-hero-chip home-hero-oars">
                  <GiPaddles />
                  <span className="home-hero-oars-name">{nextBoatAssignment.oars}</span>
                </span>
              )}
            </div>
            <div className="home-hero-actions">
              <button
                className={`home-hero-btn primary rsvp-status-${primaryEvent.rsvpStatus ? primaryEvent.rsvpStatus.toLowerCase().replace(/\s+/g, '') : 'noresponse'}`}
                onClick={onRsvpClick}
                disabled={!onRsvpClick}
              >
                <FaCheck style={{ marginRight: 6 }} />
                RSVP ({primaryEvent.rsvpStatus ? getRsvpLabel(primaryEvent.rsvpStatus) : "No response"})
              </button>
              <button
                className="home-hero-btn ghost"
                onClick={onPracticeClick ? () => handleNextEventClick(primaryEvent) : undefined}
                disabled={!onPracticeClick}
              >
                Details
              </button>
              {nextLineupPublished && (
                <button
                  className="home-hero-btn lineup"
                  onClick={onLineupClick ? () => onLineupClick(primaryEvent.date) : undefined}
                  disabled={!onLineupClick}
                >
                  <FaSitemap style={{ marginRight: 6 }} />
                  Open Lineup
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="home-hero-empty">No upcoming events</div>
        )}
      </div>

      {/* Glanceable stats */}
      <div className="home-stat-strip home-stat-strip-4">
        <div className="home-stat-cell">
          <div className="v">{loadingStats || stats.attendancePercent === null ? "—" : `${stats.attendancePercent}%`}</div>
          <div className="l">Attendance</div>
        </div>
        <div className="home-stat-cell">
          <div className="v">{loadingStats ? "—" : formatNumber(stats.practicesAttended)}</div>
          <div className="l">Practices</div>
        </div>
        <div className="home-stat-cell">
          <div className="v">{loadingStats ? "—" : formatNumber(stats.workoutsLogged)}</div>
          <div className="l">Workouts</div>
        </div>
        <div className="home-stat-cell">
          <div className="v">{loadingStats ? "—" : formatNumber(stats.metersLogged)}</div>
          <div className="l">Meters</div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="home-quick-actions">
        <button className="home-qa-btn" onClick={onRsvpClick} disabled={!onRsvpClick}>
          <span className="home-qa-icon" style={{ background: "var(--success-bg)", color: "var(--success)" }}><FaCheck /></span>
          <span className="home-qa-label">Availability</span>
        </button>
        <button className="home-qa-btn" onClick={onLogWorkoutClick} disabled={!onLogWorkoutClick}>
          <span className="home-qa-icon" style={{ background: "rgba(79,209,249,0.16)", color: "var(--accent)" }}><FaDumbbell /></span>
          <span className="home-qa-label">Log Workout</span>
        </button>
        <button
          className="home-qa-btn"
          onClick={onLineupClick ? () => onLineupClick(nextEventDate || ymd(new Date())) : undefined}
          disabled={!onLineupClick}
        >
          <span className="home-qa-icon" style={{ background: "rgba(79,140,249,0.16)", color: "var(--primary)" }}><FaSitemap /></span>
          <span className="home-qa-label">Lineups</span>
        </button>
      </div>

      {/* Regatta Schedule */}
      <div>
        <div className="home-section-label">
          <span className="t"><FaTrophy />Regatta Schedule</span>
        </div>
        <div className="home-panel">
          {loadingNextEvent ? (
            <div className="help">Loading...</div>
          ) : regattas.length === 0 ? (
            <div className="help">No upcoming regattas</div>
          ) : (
            <div className="home-regatta-scroll">
              {regattas.map(r => {
                const isAvailable = r.rsvpStatus === "Available" || r.rsvpStatus === "available";
                const isNotAvailable = r.rsvpStatus === "Not Available" || r.rsvpStatus === "not available";
                const rsvpClass = isAvailable ? "rsvp-available" : isNotAvailable ? "rsvp-notavailable" : "rsvp-none";
                const rsvpLabel = isAvailable ? "Available" : isNotAvailable ? "Not Available" : "No response";
                return (
                  <div
                    key={r.id}
                    className="home-regatta-chip"
                    onClick={onPracticeClick ? () => handleNextEventClick(r) : undefined}
                  >
                    <div className="rd">{formatRegattaDateRange(r.date, r.endDate || r.end_date)}</div>
                    <div className="rn">{r.name || "Regatta"}</div>
                    <span className={`rs ${rsvpClass}`}>{rsvpLabel}</span>
                    <div className="home-regatta-chip-actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        className={`availability-btn availability-btn-available ${isAvailable ? "active" : ""}`}
                        onClick={() => handleRegattaRsvp(r.id, "Available")}
                        aria-pressed={isAvailable}
                        disabled={!!savingRegatta[r.id]}
                      >
                        ✓
                      </button>
                      <button
                        className={`availability-btn availability-btn-unavailable ${isNotAvailable ? "active" : ""}`}
                        onClick={() => handleRegattaRsvp(r.id, "Not Available")}
                        aria-pressed={isNotAvailable}
                        disabled={!!savingRegatta[r.id]}
                      >
                        ✗
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Leaderboard + Recent activity, side by side on wider screens */}
      <div className="home-secondary-grid">
        <div>
          <div className="home-section-label">
            <span className="t"><FaMedal />Leaderboard</span>
            <div className="leaderboard-period-toggle">
              <button
                className={`leaderboard-period-btn${leaderboardPeriod === "30d" ? " active" : ""}`}
                onClick={() => handleLeaderboardPeriodChange("30d")}
              >30d</button>
              <button
                className={`leaderboard-period-btn${leaderboardPeriod === "all" ? " active" : ""}`}
                onClick={() => handleLeaderboardPeriodChange("all")}
              >All</button>
            </div>
          </div>
          <div className="home-panel">
            <div className="leaderboard-tabs">
              {LEADERBOARD_TABS.map(tab => (
                <button
                  key={tab.key}
                  className={`leaderboard-tab${leaderboardTab === tab.key ? " active" : ""}`}
                  onClick={() => handleLeaderboardTabChange(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {loadingLeaderboard ? (
              <div className="help">Loading...</div>
            ) : leaderboard.length === 0 ? (
              <div className="help">No workout data yet</div>
            ) : (
              <>
                {topThree.length > 0 && (
                  <div className="home-podium">
                    {podiumOrder.filter(i => topThree[i]).map(i => {
                      const athlete = topThree[i];
                      const slotClass = i === 0 ? "gold" : i === 1 ? "silver" : "bronze";
                      const valueText = athlete.meters > 0
                        ? `${formatNumber(athlete.meters)}m`
                        : `${athlete.workouts} wo`;
                      return (
                        <div key={athlete.id} className={`home-podium-slot ${slotClass}`}>
                          <div className="home-podium-avatar">{i + 1}</div>
                          <div className="home-podium-name">{athlete.name}{athlete.id === userId ? " (you)" : ""}</div>
                          <div className="home-podium-val">{valueText}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {rest.map((athlete, idx) => {
                  const isMe = athlete.id === userId;
                  return (
                    <div key={athlete.id} className={`home-lb-row${isMe ? " me" : ""}`}>
                      <span className="home-lb-rank">{idx + 4}</span>
                      <span className="home-lb-name">{athlete.name}{isMe ? " (you)" : ""}</span>
                      <span className="home-lb-val">
                        {athlete.meters > 0
                          ? `${formatNumber(athlete.meters)} m`
                          : `${athlete.workouts} workout${athlete.workouts !== 1 ? "s" : ""}`
                        }
                      </span>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>

        <div>
          <div className="home-section-label">
            <span className="t"><FaHistory />Recent Activity</span>
          </div>
          <RecentRsvpsTile
            team={userTeam}
            className="home-panel home-activity-panel"
            onMaintenanceClick={onMaintenanceClick}
            onPracticeClick={onPracticeClick}
            onOyoWorkoutClick={onOyoWorkoutClick}
            onLineupClick={onLineupClick}
            userRole="athlete"
          />
        </div>
      </div>
    </div>
  );
}

